import { FC } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrency, formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';
import { TradingOffer } from '../../../types/TradingOffer';

type TradingOfferDetailSummaryCardProps = {
  offer: TradingOffer;
  offerReference: string;
  availableShares: number;
  daysRemaining: number;
};

const TradingOfferDetailSummaryCard: FC<TradingOfferDetailSummaryCardProps> = ({
  offer,
  offerReference,
  availableShares,
  daysRemaining,
}) => {
  const { t } = useTranslate();

  const isSellOffer = offer.type === 'SELL';
  const totalAmount = offer.sharesCount.total * offer.sharePrice;

  const shareClassName =
    typeof offer.shareClass === 'string' ? '' : offer.shareClass?.name || '';

  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="card-title mb-0">
            <em className="icon ni ni-file-text text-primary mr-2" />
            {t('OfferSummary')}
          </h6>
          <span className="text-soft fs-12px">{offerReference}</span>
        </div>

        <div className="row g-4">
          <div className="col-6 col-md-4">
            <div
              className="pl-3"
              style={{ borderLeft: '3px solid var(--primary)' }}
            >
              <span className="overline-title text-soft">
                {t('OfferType')}
              </span>
              <h5 className="mb-0 mt-1">
                {isSellOffer ? t('Sale') : t('Purchase')}
              </h5>
              {shareClassName && (
                <span className="text-soft fs-13px">{shareClassName}</span>
              )}
            </div>
          </div>

          <div className="col-6 col-md-4">
            <div
              className="pl-3"
              style={{ borderLeft: '3px solid #1ee0ac' }}
            >
              <span className="overline-title text-soft">
                {t('SharesOffered')}
              </span>
              <h5 className="mb-0 mt-1">
                {formatNumber(offer.sharesCount.total)}{' '}
                {t('Shares').toLowerCase()}
              </h5>
              <span className="text-soft fs-13px">
                {t('Available')}:{' '}
                <strong>{formatNumber(availableShares)}</strong>
              </span>
            </div>
          </div>

          <div className="col-6 col-md-4">
            <div
              className="pl-3"
              style={{ borderLeft: '3px solid #f4bd0e' }}
            >
              <span className="overline-title text-soft">
                {t('PricePerShare')}
              </span>
              <h5 className="mb-0 mt-1">{formatCurrency(offer.sharePrice)}</h5>
              <span className="text-soft fs-13px">
                {t('Total')}: {formatCurrency(totalAmount)}
              </span>
            </div>
          </div>

          <div className="col-6 col-md-4">
            <div
              className="pl-3"
              style={{ borderLeft: '3px solid #09c2de' }}
            >
              <span className="overline-title text-soft">
                {t('StartDate')}
              </span>
              <h5 className="mb-0 mt-1">
                {format(new Date(offer.startDate), dateFormat)}
              </h5>
            </div>
          </div>

          <div className="col-6 col-md-4">
            <div
              className="pl-3"
              style={{ borderLeft: '3px solid #e85347' }}
            >
              <span className="overline-title text-soft">{t('EndDate')}</span>
              <h5 className="mb-0 mt-1">
                {format(new Date(offer.endDate), dateFormat)}
              </h5>
              {daysRemaining > 0 ? (
                <span className="text-danger fs-13px">
                  <em className="icon ni ni-clock mr-1" />
                  {daysRemaining} {t('DaysRemaining').toLowerCase()}
                </span>
              ) : (
                <span className="text-danger fs-13px">{t('Expired')}</span>
              )}
            </div>
          </div>

          <div className="col-6 col-md-4">
            <div
              className="pl-3"
              style={{ borderLeft: '3px solid #8091a7' }}
            >
              <span className="overline-title text-soft">
                {t('PartialSale')}
              </span>
              <h5 className="mb-0 mt-1">
                <span
                  className={`badge badge-dim ${
                    offer.isPartialAmountEnabled
                      ? 'badge-success'
                      : 'badge-secondary'
                  }`}
                >
                  {offer.isPartialAmountEnabled
                    ? t('YesAllowed')
                    : t('NotAllowed')}
                </span>
              </h5>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingOfferDetailSummaryCard;
